import { useRouter } from 'next/navigation';
import {
  NestedHome,
  ImageType,
  InsertHome,
  InsertAddress,
} from '@habity/types';
import { ROUTES, HOME_SELECT_FIELDS } from '@habity/constants';
import { createClient } from '~/lib/providers/supabase/client';
import { useVitec } from './providers/useVitec';
import { deleteAPI, patchAPI } from '~/lib/api';
import { getHomePath } from '@habity/services';
import { revalidateHome } from '~/app/actions/revalidate';
import {
  uploadImageToCloudflare,
  deleteImageFromCloudflare,
} from '~/lib/api/cloudflare-images';

export const useHomes = () => {
  const { push, refresh } = useRouter();
  const supabaseClient = createClient();
  const { syncEstate } = useVitec();
  // const useLegacyProfile = useUIStore((state) => state.useLegacyProfile);

  const openHome = (home: NestedHome, revalidate = true) => {
    push(getHomePath(home));
    if (revalidate) revalidateHome(home);
  };

  const viewHome = (home: NestedHome) => {
    push(ROUTES.DASHBOARD.HOMES.VIEW_PATH + home.id);
  };

  const viewHomeDashboard = (home: NestedHome) => {
    push(ROUTES.DASHBOARD.HOMES.VIEW_PATH + home.id);
  };

  const viewHomeBuyers = (home: NestedHome) => {
    push(ROUTES.DASHBOARD.HOMES.VIEW_BUYERS_PATH + home.id);
  };

  const addHome = (homes?: NestedHome[] | null) => {
    return homes && homes.length > 0 ? push(ROUTES.SELL) : push(ROUTES.CLAIM);
  };

  const updateHome = async (
    homeId: number,
    homeData: Partial<InsertHome | null>,
    addToVitec = false
  ) => {
    const supabaseClient = createClient();

    // Update home directly using Supabase client
    const updateData = {
      ...homeData,
      updated_at: new Date().toISOString(),
    };

    const { data: home, error } = await supabaseClient
      .from('homes')
      .update(updateData)
      .eq('id', homeId)
      .select(HOME_SELECT_FIELDS)
      .returns<NestedHome[] | null>()
      .maybeSingle();

    if (error) {
      console.error('Error updating home:', { error });
    }

    // Revalidate cache for home and refresh
    if (!error && home) {
      await revalidateHome(home);
      refresh();
    }

    // Sync with Vitec if requested
    if (addToVitec && home) {
      syncEstate(home);
    }

    return {
      error,
      home,
    };
  };

  const updateAddress = async (
    addressId: number,
    addressData: Partial<InsertAddress>
  ) => {
    // Update the address fields using the Hono backend
    const { error, data } = await patchAPI(
      `/providers/supabase/addresses/${addressId}`,
      JSON.stringify(addressData)
    );

    // Revalidate the home associated with this address
    if (!error) {
      // Get the home associated with this address to revalidate it
      // SUPABASE: Call backend fn instead of sb directly
      const { data: homes } = await supabaseClient
        .from('homes')
        .select('*')
        .eq('address_id', addressId)
        .limit(1);

      if (homes && homes.length > 0) {
        await revalidateHome(homes[0]);
      }

      // Refresh the page to show updated data
      refresh();
    }

    return { error, data };
  };

  const deleteHome = async (addressId: number) => {
    // Delete the address, which will cascade delete the home and the sellers
    const { error } = await deleteAPI(
      `/providers/supabase/addresses/${addressId}`
    );
    refresh();
    return { error };
  };

  const uploadImage = async (
    homeId: number,
    imageFile: File,
    agencyId: number,
    sortOrder?: number
  ): Promise<{ data: ImageType | null; error: string | null }> => {
    console.log('[uploadImage] Starting upload', { homeId, agencyId, sortOrder, fileName: imageFile.name });

    const { data: { session } } = await supabaseClient.auth.getSession();

    if (!session?.access_token) {
      console.error('[uploadImage] Not authenticated - no session token');
      return { data: null, error: 'Not authenticated' };
    }

    console.log('[uploadImage] Session found, calling uploadImageToCloudflare');

    const result = await uploadImageToCloudflare({
      token: session.access_token,
      file: imageFile,
      homeId,
      agencyId,
      sortOrder,
    });

    if (result.success) {
      refresh();
      return { data: result.image, error: null };
    }

    return { data: null, error: result.error };
  };

  const removeImage = async (agencyId: number, image: ImageType): Promise<{ error: string | null }> => {
    const { data: { session } } = await supabaseClient.auth.getSession();

    if (!session?.access_token) {
      return { error: 'Not authenticated' };
    }

    const result = await deleteImageFromCloudflare(session.access_token, agencyId, image.id);

    if (result.success) {
      refresh();
      return { error: null };
    }

    return { error: result.error };
  };

  return {
    openHome,
    viewHome,
    viewHomeDashboard,
    viewHomeBuyers,
    addHome,
    updateHome,
    updateAddress,
    deleteHome,
    uploadImage,
    removeImage,
  };
};
